import { lazy } from "react";
import getContext from "@utils/context";
import withSideEffect from "@utils/withSideEffect";
import { updateStoreWithServerContext } from "@utils/initialization";

/**
 * Get the lazy loaded component for the intended route. If it fails to do so,
 * reset to the baseline and track error.
 *
 * @param {function} dynamicImport
 * @returns {React.Component}
 */
function getLazyComponent(dynamicImport) {
  const lazyLoadedComponent = lazy(() =>
    dynamicImport().catch((err) => {
      console.info(
        `Lazy loading failed due to ${err.name}. Error: ${err.message}`
      );

      // If loading the baseline fails, then send the user to /error.
      window.location = `/error.html${window.location.search}`;
    })
  );

  // NOTE(Patrick): Using the withSideEffect HOC. I'm tapping into the route navigation
  // here because it's the most convenient way to ensure we're re-fetching
  // serverContext as a user navigates from one of these main routes to the next.
  // The alternative is to pass reloadServerContext as a prop to each sub App
  // and fire it in componentDidMount. There are no hooks provided by react-router.
  // Components down the chain should not have to worry about managing serverContext
  // and should only access it via Redux (and window.serverContext but that will be depricated).
  return withSideEffect(lazyLoadedComponent, reloadServerContext);
}

// Small value to prevent reloadServerContext from firing the first time.
let FIRST_ROUTE_LOADED = false;

/**
 * Refetch serverContext and mount it where expected.
 */
async function reloadServerContext() {
  // NOTE(Patrick): When loading the first route, don't refetch the serverContext
  // because we fetch it in initializeApplication.
  if (!FIRST_ROUTE_LOADED) {
    FIRST_ROUTE_LOADED = true;
    return;
  }
  const serverContext = await getContext();
  window.serverContext = serverContext;
  updateStoreWithServerContext(serverContext);
}

const ROUTES = [
  {
    path: "/",
    exact: true,
    component: getLazyComponent(() =>
      import(`./funnels/buyflow-en/BuyflowENApp`)
    ),
  },
  {
    path: "/(en|en-GB|en-AU|en-NZ|en-IE)",
    exact: true,
    component: getLazyComponent(() =>
      import(`./funnels/buyflow-en/BuyflowENApp`)
    ),
  },
  {
    path: "/programs/health-weight/:routeId",
    exact: true,
    component: getLazyComponent(() =>
      import(`./funnels/buyflow-en/BuyflowENApp`)
    ),
  },
  {
    path: "/(en|en-GB|en-AU|en-NZ|en-IE)/programs/health-weight/:routeId",
    exact: true,
    component: getLazyComponent(() =>
      import(`./funnels/buyflow-en/BuyflowENApp`)
    ),
  },
  {
    path: "/(es|de|de-DE|es-ES)",
    exact: true,
    component: getLazyComponent(() =>
      import(`./funnels/buyflow-esde/BuyflowESDEApp`)
    ),
  },
  {
    path: "/(es|de|de-DE|es-ES)/programs/health-weight/:routeId",
    exact: true,
    component: getLazyComponent(() =>
      import(`./funnels/buyflow-esde/BuyflowESDEApp`)
    ),
  },
  {
    path: "/(es|de|de-DE|es-ES)/healthy-weight-program",
    exact: true,
    component: getLazyComponent(() =>
      import(`./funnels/buyflow-esde/BuyflowESDEApp`)
    ),
  },
  {
    path: "/(ja|jp|ja-JP)",
    exact: true,
    component: getLazyComponent(() =>
      import(`./funnels/buyflow-ja/BuyflowJAApp`)
    ),
  },
  {
    path: "/(ja|jp|ja-JP)/programs/health-weight/:routeId",
    exact: true,
    component: getLazyComponent(() =>
      import(`./funnels/buyflow-ja/BuyflowJAApp`)
    ),
  },
  {
    path: "/(ja|jp|ja-JP)/healthy-weight-program",
    exact: true,
    component: getLazyComponent(() =>
      import(`./funnels/buyflow-ja/BuyflowJAApp`)
    ),
  },
  {
    path: "/purchase/ja/:planId",
    exact: true,
    component: getLazyComponent(() =>
      import(`./funnels/payment-ja/PaymentJAApp`)
    ),
  },
  {
    path: "/purchase/jp/:planId",
    exact: true,
    component: getLazyComponent(() =>
      import(`./funnels/payment-ja/PaymentJAApp`)
    ),
  },
  {
    path: "/purchase/en/:planId",
    exact: true,
    component: getLazyComponent(() =>
      import(`./funnels/payment-en/PaymentENApp`)
    ),
  },
  {
    path: "/purchase/(es|de)/:planId",
    exact: true,
    component: getLazyComponent(() =>
      import(`./funnels/payment-esde/PaymentESDEApp`)
    ),
  },
  {
    path: "/purchase/:planId",
    exact: true,
    component: getLazyComponent(() =>
      import(`./funnels/payment-en/PaymentENApp`)
    ),
  },
  {
    path: "/purchase-hm/:planId",
    exact: true,
    component: getLazyComponent(() =>
      import(`./funnels/hm-payment-en/HMPaymentENApp`)
    ),
  },
  {
    path: "/error",
    exact: true,
    component: () => {
      window.location = `/error.html${window.location.search}`;
    },
  },
  {
    path: "/ko/careers",
    component: getLazyComponent(() => import(`./funnels/careers-ko/Careers`)),
  },
  {
    path: "/share",
    component: getLazyComponent(() => import(`./funnels/share/Share`)),
  },
  {
    path: "/:subfolder?/careers",
    component: getLazyComponent(() => import(`./funnels/careers/Careers`)),
  },
  {
    path: "/news/noom-raises-58-million-led-by-sequoia",
    exact: true,
    component: getLazyComponent(() => import(`./funnels/careers/Careers`)),
  },
  {
    path: "/news/noom-quadruples-revenue-237-million",
    exact: true,
    component: getLazyComponent(() => import(`./funnels/careers/Careers`)),
  },
  {
    path: "/:subfolder?/about",
    exact: true,
    component: getLazyComponent(() => import(`./funnels/careers/About`)),
  },
  {
    path: "/noom-premium",
    exact: true,
    component: getLazyComponent(() =>
      import(`./funnels/noom-premium/NoomPremiumOffer`)
    ),
  },
  {
    path: "/offers",
    exact: true,
    component: getLazyComponent(() => import(`./funnels/offers/OffersENApp`)),
  },
  {
    path: "/en/offers",
    exact: true,
    component: getLazyComponent(() => import(`./funnels/offers/OffersENApp`)),
  },
  {
    path: "/add-ons",
    exact: true,
    component: getLazyComponent(() => import(`./funnels/offers/OffersENApp`)),
  },
  {
    path: "/en/add-ons",
    exact: true,
    component: getLazyComponent(() => import(`./funnels/offers/OffersENApp`)),
  },
  {
    path: "/in-app-offers",
    exact: true,
    component: getLazyComponent(() => import(`./funnels/offers/OffersENApp`)),
  },
  {
    path: "/enrollment",
    exact: true,
    component: getLazyComponent(() =>
      import(`./funnels/web-enrollment/WebEnrollmentApp`)
    ),
  },
  {
    path: "/enrollment-hm",
    exact: true,
    component: getLazyComponent(() =>
      import(`./funnels/web-enrollment-hm/WebEnrollmentAppHM`)
    ),
  },
  {
    path: "/cancel",
    exact: true,
    component: getLazyComponent(() =>
      import(`./funnels/counteroffer/CounterOffer`)
    ),
  },
  {
    path: "/ko",
    exact: true,
    component: getLazyComponent(() =>
      import(`./funnels/buyflow-ko/BuyflowKOApp`)
    ),
  },
  {
    path: "/purchase/ko/:planId",
    component: getLazyComponent(() =>
      import(`./funnels/payment-ko/PaymentKOApp`)
    ),
  },
  {
    path: "/ko/programs",
    component: getLazyComponent(() =>
      import(`./funnels/buyflow-ko/BuyflowKOApp`)
    ),
  },
  {
    path: "/fast-forward-trial",
    exact: true,
    component: getLazyComponent(() =>
      import(`./funnels/fast-forward-trial/FastForwardTrialENApp`)
    ),
  },
  {
    path: "/course",
    exact: true,
    component: getLazyComponent(() => import(`./funnels/course/CourseENApp`)),
  },
  {
    path: "/programs/healthy-mind/:routeId",
    exact: true,
    component: getLazyComponent(() =>
      import(`./funnels/buyflow-en-hm/HMBuyflowENApp`)
    ),
  },
  {
    path: "/programs/healthy-mind/",
    exact: true,
    component: getLazyComponent(() =>
      import(`./funnels/buyflow-en-hm/HMBuyflowENApp`)
    ),
  },
  {
    path: "/ccpa-do-not-sell",
    exact: true,
    component: getLazyComponent(() => import(`./funnels/ccpa/pages/DoNotSell`)),
  },
  {
    path: "/withings-scale",
    exact: true,
    component: getLazyComponent(() =>
      import(`./funnels/withings-scale/WithingsScaleENApp`)
    ),
  },
];

export default ROUTES;
